import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { OrderItem, OrderSupportRequest, OrderSupportRequestItem } from "@/lib/api";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { CancellationStepper, type CancellationStep } from "../OrderCancellation/CancellationStepper";
import { formatStepDate, isTicketResolved } from "../OrderCancellation/cancellation";
import { RequestStatusBadge } from "./OrderRequestsList";
import { RequestAttachments } from "./RequestAttachments";
import { RequestComments } from "./RequestComments";
import {
  describeOrderItem,
  formatQuantityDifference,
  getItemDifference,
  getRequestItems,
  getRequestStatus,
  isConferenceRequest,
  supportRequestQueryKeys,
} from "./supportRequest";

interface ItemRequestDetailProps {
  orderId: string;
  request: OrderSupportRequest;
  /** Itens do pedido — completam a linha de cada item com segmento, série e uso. */
  orderItems?: OrderItem[];
  getItemUsage?: (item: OrderItem) => string | null | undefined;
  isRefreshing: boolean;
  onRefresh: () => void;
}

const ItemCell = ({ item, details }: { item: OrderSupportRequestItem; details: string | null }) => (
  <td className="py-2.5 pr-3">
    <span className="block font-semibold">{item.productDescription ?? "—"}</span>
    {details && <span className="block text-[13px] text-muted-foreground">{details}</span>}
  </td>
);

/**
 * Acompanhamento de uma solicitação (troca, divergência ou outro tipo), com todos os itens do chamado. O status vem
 * ao vivo do Zendesk e pode faltar (`ticketStatus` nulo): nesse caso a régua fica em andamento e o selo avisa que o
 * status está indisponível.
 */
export const ItemRequestDetail = ({
  orderId,
  request,
  orderItems = [],
  getItemUsage,
  isRefreshing,
  onRefresh,
}: ItemRequestDetailProps) => {
  const queryClient = useQueryClient();
  const status = getRequestStatus(request.ticketStatus);
  const isResolved = isTicketResolved(request.ticketStatus);
  const items = getRequestItems(request);
  // Divergência (protótipo 2c): pedido x recebido. Troca: quantidade informada de cada item.
  const isConference = isConferenceRequest(request);
  const openedAt = request.createdAt ? formatDate(new Date(request.createdAt)) : null;

  const steps: CancellationStep[] = [
    { label: "Solicitado", caption: formatStepDate(request.createdAt) ?? "—", state: "done" },
    { label: "Em Andamento", caption: isResolved ? "Concluído" : "", state: isResolved ? "done" : "current" },
    {
      label: "Solicitação concluída",
      caption: isResolved ? "Concluído" : "Aguardando",
      state: isResolved ? "final" : "pending",
    },
  ];

  // Status (lista) e respostas (comentários) vêm do Zendesk: "Atualizar status" refaz os dois.
  const handleRefresh = () => {
    onRefresh();
    queryClient.invalidateQueries({ queryKey: supportRequestQueryKeys.comments(orderId, request.zendeskTicketId) });
  };

  // "Segmento · Série · Uso · SKU" quando o item ainda está no pedido; senão, só o SKU.
  const detailsOf = (item: OrderSupportRequestItem) => {
    const orderItem = orderItems.find((candidate) => candidate.id === item.purchaseOrderItemId);
    if (orderItem) return describeOrderItem(orderItem, getItemUsage?.(orderItem));
    return item.productSku ? `SKU ${item.productSku}` : null;
  };

  return (
    <div className="flex flex-col gap-5 p-7">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-[22px] font-bold leading-tight">{request.title}</h2>
          <p className="text-[13px] text-muted-foreground">
            {[openedAt && `Aberta em ${openedAt}`, `Ticket #${request.zendeskTicketId}`].filter(Boolean).join(" · ")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="shrink-0 hover:bg-muted hover:text-foreground"
        >
          <RefreshCw className={cn("mr-2 h-4 w-4", isRefreshing && "animate-spin")} aria-hidden="true" />
          Atualizar status
        </Button>
      </div>

      <section className="flex flex-col gap-6 rounded-[10px] border px-6 py-5">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-[19px] font-bold leading-tight">Status da solicitação</h3>
          <RequestStatusBadge status={status} className="py-1.5" />
        </div>
        <CancellationStepper steps={steps} ariaLabel="Etapas da solicitação" />
      </section>

      <section className="flex flex-col gap-2.5 rounded-[10px] border px-5 py-4">
        <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {isConference ? "Conferência de quantidade" : items.length > 1 ? "Itens" : "Item"}
        </h3>
        {items.length === 0 ? (
          <p className="text-[15px] font-semibold">Pedido inteiro</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th scope="col" className="pb-1.5 font-normal">Item</th>
                  {isConference ? (
                    <>
                      <th scope="col" className="w-[70px] pb-1.5 font-normal">Pedido</th>
                      <th scope="col" className="w-[80px] pb-1.5 font-normal">Recebido</th>
                      <th scope="col" className="w-[80px] pb-1.5 font-normal">Diferença</th>
                    </>
                  ) : (
                    <>
                      <th scope="col" className="w-[120px] pb-1.5 font-normal">Quantidade</th>
                      <th scope="col" className="w-[70px] pb-1.5 font-normal">Pedido</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const difference = getItemDifference(item);
                  return (
                    <tr key={item.purchaseOrderItemId} className="border-t">
                      <ItemCell item={item} details={detailsOf(item)} />
                      {isConference ? (
                        <>
                          <td className="py-2.5">{item.orderedQuantity ?? "—"}</td>
                          <td className="py-2.5">{item.receivedQuantity ?? "—"}</td>
                          <td
                            className={cn(
                              "py-2.5 font-bold",
                              difference === 0 ? "text-primary" : "text-amber-700 dark:text-amber-300"
                            )}
                          >
                            {difference === null ? "—" : difference === 0 ? "OK" : formatQuantityDifference(difference)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="whitespace-nowrap py-2.5 pr-3 font-bold">
                            {item.quantity ?? <span className="font-normal text-muted-foreground">Não informada</span>}
                          </td>
                          <td className="py-2.5">{item.orderedQuantity ?? "—"}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {request.description && (
        <section className="flex flex-col gap-1 rounded-[10px] border bg-muted/30 px-5 py-4">
          <h3 className="text-[13px] text-muted-foreground">{isConference ? "Observações" : "Descrição"}</h3>
          <p className="whitespace-pre-line break-words text-[14.5px] leading-relaxed">{request.description}</p>
        </section>
      )}

      <RequestAttachments orderId={orderId} zendeskTicketId={request.zendeskTicketId} />

      <RequestComments orderId={orderId} zendeskTicketId={request.zendeskTicketId} />
    </div>
  );
};
